<script lang="ts">
    import { AskPositionMessage_AskType, AvailabilityStatus } from "@workadventure/messages";
    import * as Sentry from "@sentry/svelte";
    import highlightWords from "highlight-words";
    import { defaultColor } from "@workadventure/shared-utils";
    import { localUserStore } from "../../../Connection/LocalUserStore";
    import { availabilityStatusStore } from "../../../Stores/MediaStore";
    import { getColorHexOfStatus } from "../../../Utils/AvailabilityStatus";
    import type { ChatUser } from "../../Connection/ChatConnection";
    import { LL } from "../../../../i18n/i18n-svelte";
    import { chatSearchBarValue } from "../../Stores/ChatStore";
    import { resolveChatUserColor } from "../../Connection/Matrix/services/WaMatrixProfileService";
    import { getMatrixClientForChatTint, openDirectChatRoom } from "../../Utils";
    import { gameManager } from "../../../Phaser/Game/GameManager";
    import { analyticsClient } from "../../../Administration/AnalyticsClient";
    import { createFloatingUiActions } from "../../../Utils/svelte-floatingui";
    import Avatar from "../Avatar.svelte";
    import UserActionButton from "./UserActionButton.svelte";
    import { IconLoader, IconSend } from "@wa-icons";

    interface Props {
        user: ChatUser;
        isMatrixChatEnabled: boolean;
    }

    let { user, isMatrixChatEnabled = true }: Props = $props();

    let showRoomCreationInProgress = false;

    let { chatId, availabilityStatus, username = "", color, isAdmin, pictureStore } = $derived(user);

    /** Tint: local name, Matrix `account_data`, or peer cache — deps keep the row in sync. */
    let resolvedAvatarColor = $derived(
        chatId !== undefined && chatId !== ""
            ? (resolveChatUserColor(chatId, color, getMatrixClientForChatTint()) ?? defaultColor)
            : defaultColor,
    );

    let isMe = $derived(
        user.chatId === localUserStore.getChatId() || user.uuid === localUserStore.getLocalUser()?.uuid,
    );

    let userStatus = $derived(isMe ? availabilityStatusStore : availabilityStatus);

    let sendButtonTooltipVisible = $state(false);
    const [sendButtonFloatingRef, sendButtonFloatingContent, sendButtonArrowAction] = createFloatingUiActions(
        { placement: "top" },
        8,
    );

    let chunks = $derived(
        highlightWords({
            text: username.match(/\[\d*]/) ? username.substring(0, username.search(/\[\d*]/)) : username,
            query: $chatSearchBarValue,
        }),
    );

    const roomCreationInProgress = gameManager.chatConnection.roomCreationInProgress;

    function getNameOfAvailabilityStatus(status: AvailabilityStatus) {
        switch (status) {
            case AvailabilityStatus.ONLINE:
                return $LL.chat.status.online();
            case AvailabilityStatus.AWAY:
                return $LL.chat.status.away();
            case AvailabilityStatus.BUSY:
                return $LL.chat.status.busy();
            case AvailabilityStatus.DO_NOT_DISTURB:
                return $LL.chat.status.do_not_disturb();
            case AvailabilityStatus.BACK_IN_A_MOMENT:
                return $LL.chat.status.back_in_a_moment();
            case AvailabilityStatus.JITSI:
            case AvailabilityStatus.BBB:
            case AvailabilityStatus.LIVEKIT:
                return $LL.chat.status.meeting();
            case AvailabilityStatus.SPEAKER:
                return $LL.chat.status.megaphone();
            case AvailabilityStatus.SILENT:
            default:
                return $LL.chat.status.unavailable();
        }
    }

    let loadingDirectRoomAccess = false;

    function openWokaMenu() {
        if (user.uuid == undefined) return;
        // Track the open woka menu action
        analyticsClient.openWokaMenu();

        const currentScene = gameManager.getCurrentGameScene();

        const remotePlayerData = currentScene.getRemotePlayersRepository().getPlayerByUuid(user.uuid);
        if (remotePlayerData != undefined) {
            const remotePlayer = currentScene.MapPlayersByKey.get(remotePlayerData.userId);
            if (remotePlayer != undefined) {
                remotePlayer.activate();
                return;
            }
        }

        currentScene.connection?.emitAskPosition(
            user.uuid ?? "",
            user.playUri ?? "",
            AskPositionMessage_AskType.LOCATE,
        );
    }
</script>

{#if loadingDirectRoomAccess}
    <div class="min-h-[60px] text-md flex gap-2 justify-center flex-row items-center p-1">
        <IconLoader class="animate-spin" />
    </div>
{:else}
    <div class="flex flex-col px-2 pb-2 user">
        <div
            class="wa-chat-item {isAdmin
                ? 'admin'
                : 'user'} group/chatItem relative mb-[1px] text-md flex gap-2 flex-row items-center hover:bg-white transition-all hover:bg-opacity-10 hover:rounded hover:!cursor-pointer px-2 py-2 cursor-pointer"
        >
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                class="relative shrink-0 wa-avatar {!$userStatus ? 'opacity-50' : ''} cursor-pointer"
                onclick={(event) => {
                    event.stopPropagation();
                    openWokaMenu();
                }}
            >
                <Avatar compact {pictureStore} fallbackName={username || "?"} color={resolvedAvatarColor} />
            </div>
            <!-- svelte-ignore a11y_click_events_have_key_events -->
            <!-- svelte-ignore a11y_no_static_element_interactions -->
            <div
                class={`flex-auto ms-1 ${!$userStatus && "opacity-50"} cursor-pointer`}
                onclick={(event) => {
                    event.stopPropagation();
                    openWokaMenu();
                }}
            >
                <div class="flex items-center h-4">
                    <div class="text-sm font-bold mb-0 flex items-center text-nowrap">
                        {#each chunks as chunk (chunk.key)}
                            <div class={`${chunk.match ? "text-light-blue" : ""}`}>
                                {chunk.text}
                            </div>
                        {/each}
                        {#if username && username.match(/\[\d*]/)}
                            <div class="font-light text-xs text-gray">
                                #{username
                                    .match(/\[\d*]/)
                                    ?.join()
                                    ?.replace("[", "")
                                    ?.replace("]", "")}
                            </div>
                        {/if}
                        {#if isAdmin}
                            <div
                                class="text-xxs bg-secondary rounded-sm px-1 py-0.5 ms-1"
                                title={$LL.chat.role.admin()}
                            >
                                {$LL.chat.role.adminShort()}
                            </div>
                        {/if}
                    </div>
                </div>
                <div class="text-xs mb-0 font-condensed opacity-75 self-end">
                    {#if isMe}
                        {$LL.chat.you()}
                    {:else if $userStatus}
                        <div class="flex items-center brightness-150" style="color:{getColorHexOfStatus($userStatus)}">
                            <div
                                class="rounded-full me-1 h-1.5 w-1.5"
                                style="background:{getColorHexOfStatus($userStatus)}"
                            ></div>
                            {getNameOfAvailabilityStatus($userStatus)}
                        </div>
                    {:else}
                        {$LL.chat.userList.disconnected()}
                    {/if}
                </div>
            </div>
            <div class="flex flex-wrap justify-start items-start content-start">
                <div class="transition-all">
                    {#if !isMe && $userStatus != 0}
                        <UserActionButton {user} />
                    {/if}
                </div>
                {#if !isMe && !showRoomCreationInProgress && isMatrixChatEnabled}
                    <!-- svelte-ignore a11y_no_static_element_interactions -->
                    <div
                        class="relative"
                        use:sendButtonFloatingRef
                        onmouseenter={() => (sendButtonTooltipVisible = true)}
                        onmouseleave={() => (sendButtonTooltipVisible = false)}
                    >
                        <button
                            class="transition-all hover:bg-white/10 p-2 rounded-md aspect-square flex items-center justify-center m-0"
                            class:text-white={user.chatId !== undefined}
                            class:text-gray-400={user.chatId === undefined}
                            data-testId={`send-message-${user.username}`}
                            disabled={user.chatId === undefined}
                            onclick={(event) => {
                                event.stopPropagation();
                                openDirectChatRoom(chatId).catch((error) => {
                                    console.error("Error opening direct chat room:", error);
                                    Sentry.captureException(error, {
                                        extra: {
                                            userId: user.uuid,
                                            chatId: chatId,
                                            playUri: user.playUri,
                                            username: user.username,
                                        },
                                    });
                                });
                                analyticsClient.sendMessageFromUserList();
                            }}
                        >
                            <IconSend font-size="16" />
                        </button>
                        {#if sendButtonTooltipVisible}
                            <div
                                use:sendButtonFloatingContent
                                class="send-button-tooltip absolute z-50 bg-contrast/90 backdrop-blur-xl text-white text-nowrap p-2 rounded text-sm"
                            >
                                <div class="!top-[30%] !-translate-x-1/2" use:sendButtonArrowAction></div>
                                {#if user.chatId === undefined}
                                    {$LL.chat.remoteUserNotConnected()}
                                {:else}
                                    {$LL.chat.userList.sendMessage()}
                                {/if}
                            </div>
                        {/if}
                    </div>
                {:else if $roomCreationInProgress && showRoomCreationInProgress}
                    <div class="min-h-[30px] text-md flex gap-2 justify-center flex-row items-center p-1">
                        <IconLoader class="animate-spin" />
                    </div>
                {/if}
            </div>
        </div>
    </div>

    <style lang="scss">
        .status {
            background-color: var(--color);
        }

        .send-button-tooltip {
            opacity: 0;
            transition: opacity 0.2s ease-in-out;
            animation: sendButtonTooltipFadeIn 0.2s forwards;
        }

        @keyframes sendButtonTooltipFadeIn {
            from {
                opacity: 0;
            }
            to {
                opacity: 1;
            }
        }
    </style>
{/if}
